// API client service for article briefs

import { API_URL } from '../../config';
import { createRetryFetch, ApiErrorClass } from '../errorHandling';
import { errorHandlingService, type ErrorHandlingResponse } from './fallbackService';
import type {
  ArticleBrief,
  BriefStatus,
  ApiResponse,
} from '../../types/article-brief';

export class ArticleBriefService {
  private baseUrl: string;
  private retryFetch: typeof fetch;
  private getAuthToken?: () => Promise<string | null>;

  constructor(baseUrl: string = API_URL, getAuthToken?: () => Promise<string | null>) {
    this.baseUrl = baseUrl;
    this.getAuthToken = getAuthToken;
    this.retryFetch = createRetryFetch({
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 5000,
    });
  }

  /**
   * Helper to get headers with authorization token
   */
  private async getHeaders(additionalHeaders: HeadersInit = {}): Promise<HeadersInit> {
    const headers: Record<string, string> = {};

    // Copy additional headers
    if (additionalHeaders) {
      if (additionalHeaders instanceof Headers) {
        additionalHeaders.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(additionalHeaders)) {
        additionalHeaders.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, additionalHeaders);
      }
    }

    // Add authorization token
    if (this.getAuthToken) {
      try {
        const token = await this.getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
        // Continue without token - backend will return 401 if required
      }
    }

    return headers;
  }

  /**
   * Get all article briefs with optional filters
   */
  async getAll(filters?: {
    status?: BriefStatus;
    limit?: number;
    offset?: number;
  }): Promise<ErrorHandlingResponse<ArticleBrief[]>> {
    const params = new URLSearchParams();

    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.offset) {
      params.append('offset', filters.offset.toString());
    }

    const url = `${this.baseUrl}/api/article-briefs${params.toString() ? `?${params.toString()}` : ''}`;
    const headers = await this.getHeaders();

    return await errorHandlingService.withErrorHandling(
      async () => {
        const response = await this.retryFetch(url, { headers });
        const result: ApiResponse<ArticleBrief[]> = await response.json();

        if (!result.success) {
          throw new ApiErrorClass(result.error || 'Failed to fetch article briefs', response.status);
        }

        return result.data || [];
      },
      {
        errorMessage: 'Unable to load article briefs'
      }
    );
  }

  /**
   * Get a single article brief by ID
   */
  async getById(id: string): Promise<ArticleBrief> {
    try {
      const headers = await this.getHeaders();
      const response = await this.retryFetch(`${this.baseUrl}/api/article-briefs/${encodeURIComponent(id)}`, { headers });
      const result: ApiResponse<ArticleBrief> = await response.json();

      if (!result.success || !result.data) {
        throw new ApiErrorClass(result.error || 'Failed to fetch article brief', response.status);
      }

      return result.data;
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        throw error;
      }
      throw new ApiErrorClass(
        error instanceof Error ? error.message : `Failed to fetch article brief: ${id}`
      );
    }
  }

  /**
   * Create a new article brief
   */
  async create(data: {
    metadata?: Record<string, unknown>,
    name: string;
    title: string;
    task: string;
    description?: string;
    urls?: string[];
  }): Promise<ArticleBrief> {
    try {
      const headers = await this.getHeaders({
        'Content-Type': 'application/json',
      });
      const response = await this.retryFetch(`${this.baseUrl}/api/article-briefs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      const result: ApiResponse<ArticleBrief> = await response.json();

      if (!result.success || !result.data) {
        throw new ApiErrorClass(result.error || 'Failed to create article brief', response.status);
      }

      return result.data;
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        throw error;
      }
      throw new ApiErrorClass(
        error instanceof Error ? error.message : 'Failed to create article brief'
      );
    }
  }

  /**
   * Update an existing article brief
   */
  async update(
    id: string,
    data: Partial<{
      name: string;
      title: string;
      task: string;
      description?: string;
      urls?: string[];
      status?: BriefStatus;
    }>
  ): Promise<ArticleBrief> {
    try {
      const headers = await this.getHeaders({
        'Content-Type': 'application/json',
      });
      const response = await this.retryFetch(`${this.baseUrl}/api/article-briefs/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });

      const result: ApiResponse<ArticleBrief> = await response.json();

      if (!result.success || !result.data) {
        throw new ApiErrorClass(result.error || 'Failed to update article brief', response.status);
      }

      return result.data;
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        throw error;
      }
      throw new ApiErrorClass(
        error instanceof Error ? error.message : 'Failed to update article brief'
      );
    }
  }

  /**
   * Check related data before deletion
   */
  async checkRelatedData(id: string): Promise<{
    hasRelatedData: boolean;
    relatedArticles: number;
    relatedOutlines: number;
    relatedSections: number;
    relatedCitations: number;
    details: {
      articles: Array<{ id: string; title: string; status: string }>;
      outlines: Array<{ id: string; createdAt: string }>;
    };
  }> {
    try {
      const headers = await this.getHeaders();
      const response = await this.retryFetch(`${this.baseUrl}/api/article-briefs/${encodeURIComponent(id)}/related-data`, { headers });
      const result: ApiResponse<any> = await response.json();

      if (!result.success || !result.data) {
        throw new ApiErrorClass(result.error || 'Failed to check related data', response.status);
      }

      return result.data;
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        throw error;
      }
      throw new ApiErrorClass(
        error instanceof Error ? error.message : 'Failed to check related data'
      );
    }
  }

  /**
   * Delete an article brief (soft delete) with optional related data check
   */
  async delete(id: string, options?: { checkRelated?: boolean }): Promise<{
    success: boolean;
    relatedDataInfo?: {
      hasRelatedData: boolean;
      relatedArticles: number;
      relatedOutlines: number;
      relatedSections: number;
      relatedCitations: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      if (options?.checkRelated) {
        params.append('checkRelated', 'true');
      }

      const headers = await this.getHeaders();
      const url = `${this.baseUrl}/api/article-briefs/${encodeURIComponent(id)}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await this.retryFetch(url, {
        method: 'DELETE',
        headers,
      });

      const result: ApiResponse<any> = await response.json();

      if (!result.success) {
        throw new ApiErrorClass(result.error || 'Failed to delete article brief', response.status);
      }

      return {
        success: true,
        relatedDataInfo: (result as any).warning?.relatedData
      };
    } catch (error) {
      // Treat 404 as successful deletion (item doesn't exist anyway)
      if (error instanceof ApiErrorClass && error.status === 404) {
        return { success: true };
      }

      if (error instanceof ApiErrorClass) {
        throw error;
      }
      throw new ApiErrorClass(
        error instanceof Error ? error.message : 'Failed to delete article brief'
      );
    }
  }

  /**
   * Hard delete an article brief and all related data
   */
  async hardDelete(id: string, options?: { force?: boolean }): Promise<{
    success: boolean;
    deletionSummary: {
      deletedBrief: boolean;
      deletedArticles: number;
      deletedOutlines: number;
      deletedSections: number;
      deletedCitations: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      if (options?.force) {
        params.append('force', 'true');
      }

      const headers = await this.getHeaders();
      const url = `${this.baseUrl}/api/article-briefs/${encodeURIComponent(id)}/hard${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await this.retryFetch(url, {
        method: 'DELETE',
        headers,
      });

      const result: ApiResponse<any> = await response.json();

      if (!result.success || !result.data) {
        throw new ApiErrorClass(result.error || 'Failed to hard delete article brief', response.status);
      }

      return {
        success: true,
        deletionSummary: result.data.deletionSummary
      };
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        throw error;
      }
      throw new ApiErrorClass(
        error instanceof Error ? error.message : 'Failed to hard delete article brief'
      );
    }
  }

  /**
   * Search article briefs by query string
   */
  async search(query: string): Promise<ArticleBrief[]> {
    try {
      const headers = await this.getHeaders();
      const params = new URLSearchParams({ q: query });
      const response = await this.retryFetch(`${this.baseUrl}/api/article-briefs/search?${params.toString()}`, { headers });
      const result: ApiResponse<ArticleBrief[]> = await response.json();

      if (!result.success) {
        throw new ApiErrorClass(result.error || 'Failed to search article briefs', response.status);
      }

      return result.data || [];
    } catch (error) {
      if (error instanceof ApiErrorClass) {
        throw error;
      }
      throw new ApiErrorClass(
        error instanceof Error ? error.message : 'Failed to search article briefs'
      );
    }
  }
}

// Export singleton instance
export const articleBriefService = new ArticleBriefService();
